import {type Feature} from '../../../values/MedullaUIConfig'

export type OAuthClientIdsType = {
    google?: string
    microsoft?: string
}

export enum ConfigFetchingStatus {
    UNINITIALISED = 'UNINITIALISED',
    REQUESTING = 'REQUESTING',
    INITIALISED = 'INITIALISED',
    NEEDS_RESETTING = 'NEEDS_RESETTING',
    REREQUESTING = 'REREQUESTING',
}

export enum LicenseFleetConfigKey {
    LICENSE_INCIDENTS = 'License:Incidents',
    LICENSE_ENFORCEMENT_NETWORKS = 'License:Enforcement:Networks',
    LICENSE_ENFORCEMENT_REMOVABLES = 'License:Enforcement:Removables',
    LICENSE_METRICS_ONBOARD_ENGAGEMENT = 'License:Metrics:OnboardEngagement',
    LICENSE_OTSM = 'License:OTSM',
    LICENSE_COMPANY_NAME = 'License:CompanyName',
}

enum OtherFleetConfigKey {
    ACTIVE_CONTROL = 'ActiveControl',
    INCIDENTS = 'Incidents',
    REPORTS = 'Reports',
    NETWORK_ASSETS = 'NetworkAssets',
    USB_INVENTORY = 'UsbInventory',
    SOFTWARE_INVENTORY = 'SoftwareInventory',
    NMEA_INVENTORY = 'NmeaInventory',
    METRICS = 'Metrics',
    INCIDENT_REOPEN_PROCESSING = 'IncidentReopenProcessing',
}

export const FleetConfigKeysType = {
    ...LicenseFleetConfigKey,
    ...OtherFleetConfigKey,
}

export type FleetConfigKey = LicenseFleetConfigKey | OtherFleetConfigKey

export function enumValues<T extends Record<string, string | number>>(e: T): T[keyof T][] {
    return Object.values(e) as T[keyof T][]
}

export type LicenseKeyShort = LicenseFleetConfigKey extends `License:${infer R}` ? R : never

type LicenseState = {
    [K in Exclude<LicenseFleetConfigKey, LicenseFleetConfigKey.LICENSE_OTSM>]?: License<
        K extends `License:${infer T}` ? T : never
    >
}

export type License<T extends LicenseKeyShort = LicenseKeyShort> = {
    identity: string
    key: `License:${T}`
    value: LicenseValue
    category: string
}

export type LicenseValue = {
    enabled?: boolean
    numberOfLicenses?: number | null
    endTimestamp?: null | Date
    companyName?: string | null
}

export interface FleetConfigStatus {
    reports: boolean
    networkAssets: boolean
    usbInventory: boolean
    softwareInventory: boolean
    nmeaInventory: boolean
    incidentReopenDuration: string | undefined
    LicenseState?: LicenseState
}

const DEFAULT_FLEET_CONFIG_STATUS: FleetConfigStatus = {
    reports: false,
    networkAssets: false,
    usbInventory: false,
    softwareInventory: false,
    nmeaInventory: false,
    incidentReopenDuration: undefined,
    LicenseState: undefined,
}
export interface UserGuidingConfig {
    userGuidingEnabled: boolean
    userGuidingId: string
}

export interface ConfigReduxState {
    splunkDashboardUrl?: string
    features?: Feature[]
    tenantDisplayName?: string
    fetchingStatus: ConfigFetchingStatus
    oauthClientIds: OAuthClientIdsType | undefined
    gfcFlag: boolean
    customLogoFlag: boolean
    fleetConfig: FleetConfigStatus
    userGuidingConfig: UserGuidingConfig | undefined
    requireStrictAccessOnExternalUsers?: boolean
    isMultiTenantEnvironment?: boolean
}

export const DEFAULT_CONFIG_STATE: ConfigReduxState = {
    features: [],
    fetchingStatus: ConfigFetchingStatus.UNINITIALISED,
    oauthClientIds: {
        google: '',
        microsoft: '',
    },
    gfcFlag: true,
    customLogoFlag: false,
    fleetConfig: DEFAULT_FLEET_CONFIG_STATUS,
    userGuidingConfig: undefined,
    requireStrictAccessOnExternalUsers: false,
    isMultiTenantEnvironment: true,
}
